// SignaturesPlaceholder.ts

import { Component, Watch } from 'vue-property-decorator'
// @ts-ignore
import VueDragResize from 'vue-drag-resize'
// @ts-ignore
import pdf from '@teckel/vue-pdf'
import _ from 'lodash'
import api from '@/utils/axios'
import { tryGetCurrentUser } from '@/utils/usersLegacy'
import PdfViewer from './PdfViewer'
import Vue from 'vue'
import { getContactValue, pdfToPixels } from '@/utils/stampUtils'
import { measureElement } from '@/infranstructure/hooks/elementBoxes';

@Component({
  components: {
    pdf,
    VueDragResize,
  },
})
export default class SignaturesPlaceholder extends PdfViewer {
  public overlay: boolean = false
  public initialized: boolean = false
  public pdfDocumentLoaded: boolean = false
  public participants: any[] = []
  public signedPages: number[] = []

  public get sessionId(): string {
    return this.$route.params.id
  }

  public get senderId(): string {
    return this.$route.params.uid
  }

  public get signatures(): any[] {
    return this.transaction.documents[0]?.signatures || []
  }

  get currentDocumentIndex(): number | undefined {
    const currentDocId = this.$store.getters.currentDocument?.id;
    if (!currentDocId) return undefined;
    return this.$store.state.transaction.documents?.findIndex((doc: any) => doc.id === currentDocId);
  }

  public get document(): any {
    if (this.currentDocumentIndex === undefined) {
      return undefined
    }
    return this.$store.getters.transaction.documents[this.currentDocumentIndex]
  }

  public get documentBlobUri(): string {
    return this.document?.blobUri || ''
  }

  public get participantsList(): any[] {
    return [this.transaction.author, ...(this.transaction.actors || [])]
  }

  public get transaction() {
    return this.$store.getters.transaction
  }

  @Watch('documentBlobUri', { immediate: true })
  public onDocumentBlobUriChanged(): void {
    this.blobUri = this.documentBlobUri
    this.pdfDocumentLoaded = false
    this.overlay = true
  }

  @Watch('pdfPages', { immediate: true })
  public async onPdfPagesChanged(): Promise<void> {
    if (this.pdfPages && this.pdfPages.length > 0) await this.initialize()
  }

  private async initialize(): Promise<void> {
    await this.loadTransaction()
    await this.loadParticipants()
    this.$store.dispatch('setFooter', true)
    this.onInitialized()
    this.initialized = true
  }

  public get allUnsignedMembers() {
    const allMembers = _.uniqBy(
      [this.transaction.author, ...this.transaction.actors, this.transaction.creator],
      'contact'
    )
    if (this.transaction.preAuthor) allMembers.push(this.transaction.preAuthor)
    const emails = _.map(
      _.uniqBy(
        _.filter(this.transaction.documents[0]?.signatures || [], (el) => el.status === 'Signing'),
        'actor'
      ),
      (e) => e.actor
    )
    return _.filter(allMembers, (el) => emails.includes(el.contact))
  }

  public get allMembers() {
    if (!this.transaction) return []
    return [...[this.transaction.author], ...(this.transaction.actors || [])]
  }

  participantRole(participant: any, i: number) {
    if (this.transaction.actors[0]?.preActor?.contact === participant.contact) return 'Утверждающий'
    if (this.transaction.preAuthor && participant.contact === this.transaction.preAuthor.contact) return 'Инициатор'
    if (this.transaction.actors.includes(participant) || this.transaction.author.contact === participant.contact)
      return 'Подписант: ' + (1 + i)
    return 'Подписант'
  }

  private async loadTransaction(): Promise<void> {
    const { sessionId, senderId } = this
    await this.$store.dispatch('uploadTransaction', { sessionId, senderId })
  }

  private async loadParticipants(): Promise<void> {
    this.participants = await Promise.all(
      _.map([this.transaction.author, ...(this.transaction.actors || [])], async (participant) => {
        const resultObject: Record<number, { 
          image?: string; 
          isVisible: boolean; 
          x?: number; 
          y?: number; 
          width?: number; 
          height?: number; 
        }> = {}

        const stampImage = await this.loadAddedUserStamp(participant)
        for (let page = 1; page <= this.countPages; page += 1) {
          Vue.set(resultObject, page, { 
            image: stampImage, 
            isVisible: false,
            x: 0,
            y: 0,
            width: 0,
            height: 0,
          })
        }
        Vue.set(participant, 'stamps', resultObject)
        return participant
      })
    )
  }

  async loadAddedUserStamp(participant: any) {
    try {
      const response = await api.get('/SignatureStamps/AddedStamp', {
        params: {
          transactionId: this.sessionId,
          actorContact: getContactValue(participant.contact),
          userId: this.senderId,
        },
      })
      return response.request.responseURL
    } catch (error) {
      console.warn('Failed to load stamp image for', participant.contact, error)
      return ''
    }
  }

  // === ОБНОВЛЁННЫЙ МЕТОД ===
  private async updateStampPositions(): Promise<void> {
    if (!this.pdfPages || this.pdfPages.length !== this.countPages) return;

    _.forEach(this.participants, (participant) => {
      _.forEach(participant.stamps, (stamp: any, pageStr: string) => {
        const page = Number(pageStr);
        if (page < 1 || page > this.countPages) return;

        const signature = this.getSignature(participant, page);
        const pdfPage = this.pdfPages[page - 1];

        if (signature && pdfPage) {
          const container = this.getPdfPageContainer(page);
          if (!container) return;

          const containerRect = container.getBoundingClientRect();
          const containerWidth = containerRect.width;
          const containerHeight = containerRect.height;

          // Размер страницы PDF в points при scale = 1
          const pdfPageWidth = pdfPage.view[2]; // x2 - x1 = ширина
          // const pdfPageHeight = pdfPage.view[3]; // не нужна напрямую

          const { x, y, width, height } = pdfToPixels(
            signature.posX,
            signature.posY,
            signature.posXEnd - signature.posX,
            signature.posYEnd - signature.posY,
            containerWidth,
            containerHeight,
            pdfPageWidth
          );

          Vue.set(stamp, 'x', x);
          Vue.set(stamp, 'y', y);
          Vue.set(stamp, 'width', width);
          Vue.set(stamp, 'height', height);
          Vue.set(stamp, 'isVisible', true);
        } else {
          Vue.set(stamp, 'isVisible', false);
        }
      });
    });

    await this.recountSignedPages();
  }

  private getRealPageDimensions(page: number): { width: number; height: number } | null {
    const container = this.getPdfPageContainer(page)
    if (!container) return null
    const boxSizes = measureElement(container)
    return {
      width: boxSizes.contentBox.width,
      height: boxSizes.contentBox.height,
    }
  }

  private getSignature(participant: any, page: number): any {
    return _.find(this.signatures, (signature) => 
      signature.page == page && signature.actor === getContactValue(participant.contact)
    )
  }

  private async recountSignedPages() {
    const user = await tryGetCurrentUser(this.sessionId, this.senderId)
    const signedPages: number[] = []
    this.transaction.documents[0]?.signatures.forEach((element: any) => {
      if (user?.contact === element.actor) signedPages.push(element.page)
    })
    this.signedPages = _.orderBy([...new Set(signedPages)])
  }

  public showSignature(participant: any): any {
    if (!participant) return

    const contact = getContactValue(participant.contact)
    const existing = this.signatures.find(
      (s) => s.actor === contact && s.page === this.currentPage
    )

    if (!existing) {
      const viewport = this.pdfPages[this.currentPage - 1]?.getViewport({ scale: 1 })
      if (!viewport) return

      const all = _.compact([this.transaction.author, ...(this.transaction.actors || [])])
      const index = all.findIndex(p => getContactValue(p.contact) === contact)
      const isOdd = (index + 1) % 2 === 1
      const stampWidth = 150
      const stampHeight = 50
      const margin = 40
      const verticalStep = stampHeight + 20
      const rowIndex = Math.floor(index / 2)
      const posY = margin + rowIndex * verticalStep

      let posX = isOdd
        ? viewport.width / 2 + (viewport.width / 4 - stampWidth / 2)
        : viewport.width / 4 - stampWidth / 2

      posX = Math.max(margin, Math.min(viewport.width - stampWidth - margin, posX))

      this.$store.commit('ADD_SIGNATURE', {
        documentIndex: 0,
        signature: {
          actor: contact,
          page: this.currentPage,
          posX,
          posY,
          posXEnd: posX + stampWidth,
          posYEnd: posY + stampHeight,
          text: 'string',
        },
      })
    }

    this.updateStampPositions()
    return participant
  }

  public onDragStamp(stamp: any, data: any): void {}
  public onResizeStamp(stamp: any, data: any): void {}
  public setSignatures(): void {}
  public putStamps(): void {}

  public onLoadedPdfPage(page: number): void {
    if (page === this.countPages) {
      this.pdfDocumentLoaded = true
      this.updateStampPositions()
    }
  }

  public onInitialized() {
    this.updateStampPositions()
    this.overlay = false
    this.$store.dispatch('setCurrentPage', 1)
    const el = document.getElementById('pageScroller') as HTMLElement
    if (el) {
      el.scrollLeft = 0
    }
  }

  public doRectanglesOverlap(rect1: any, rect2: any): boolean {
    const rect1Right = rect1.x + rect1.width
    const rect1Bottom = rect1.y + rect1.height
    const rect2Right = rect2.x + rect2.width
    const rect2Bottom = rect2.y + rect2.height

    return !(
      rect1.x >= rect2Right ||
      rect2.x >= rect1Right ||
      rect1.y >= rect2Bottom ||
      rect2.y >= rect1Bottom
    )
  }

  public checkForOverlaps(rectangles: any[]): boolean {
    for (let i = 0; i < rectangles.length; i++) {
      for (let j = i + 1; j < rectangles.length; j++) {
        if (rectangles[i].page !== rectangles[j].page) continue
        if (this.doRectanglesOverlap(rectangles[i], rectangles[j])) {
          return true
        }
      }
    }
    return false
  }

  public getPdfPageContainer(page: number): HTMLElement | null {
    const refs = this.$refs as Record<string, any>
    const key = `pdfPage__${page}`
    const containers = refs[key]
    if (!containers || !containers.length) return null
    const container = containers[0]
    return container instanceof HTMLElement ? container : container.$el || null
  }
}